import type {
  Brief,
  BriefAnswers,
  Business,
  Claim,
  CreateBusinessInput,
  CreatePerkInput,
  FavoriteTargetType,
  Link,
  NearbyBusiness,
  Perk,
  PerkWithBusiness,
  SocialLinks,
  UpdateBriefInput,
  UpdateBusinessInput,
  UpdateClaimInput,
  UpdatePerkInput,
  UpdateProfileInput,
  User,
} from "@dropin/shared/schemas";

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "";
const BASE = API_ORIGIN ? API_ORIGIN : "/api";

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export function requestOtp(email: string, intent: "creator" | "business") {
  return req<{ ok: true; devCode?: string }>("/auth/request", {
    method: "POST",
    body: JSON.stringify({ email, intent }),
  });
}

export function verifyOtp(email: string, code: string) {
  return req<{ ok: true }>("/auth/verify", {
    method: "POST",
    body: JSON.stringify({ email, code }),
  });
}

export function logout() {
  return req<{ ok: true }>("/auth/logout", { method: "POST" });
}

// Uploads — multipart/form-data, returns a public URL on assets.drpn.us
export async function uploadImage(
  file: File,
  kind: "avatars" | "business-logos" | "perk-images",
  scopeId: string,
): Promise<{ url: string; key: string }> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  fd.append("scopeId", scopeId);
  const res = await fetch(`${BASE}/uploads/image`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${body || res.statusText}`);
  }
  return res.json() as Promise<{ url: string; key: string }>;
}

export function getMe() {
  return req<{ user: User }>("/me");
}
export function updateMyProfile(input: UpdateProfileInput) {
  return req<{ user: User }>("/me", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}
export function requestVerification() {
  return req<{ user: User }>("/me/request-verification", { method: "POST" });
}

// Nearby search
export function searchNearby(opts: { lat: number; lon: number; radiusKm?: number }) {
  const params = new URLSearchParams({
    lat: String(opts.lat),
    lon: String(opts.lon),
  });
  if (opts.radiusKm) params.set("radius_km", String(opts.radiusKm));
  return req<{ businesses: NearbyBusiness[] }>(`/search/nearby?${params.toString()}`);
}

// Favorites
export function listFavorites() {
  return req<{ businessIds: string[]; perkIds: string[] }>("/favorites");
}
export function addFavorite(targetType: FavoriteTargetType, targetId: string) {
  return req<{ ok: true }>("/favorites", {
    method: "POST",
    body: JSON.stringify({ targetType, targetId }),
  });
}
export function removeFavorite(targetType: FavoriteTargetType, targetId: string) {
  return req<{ ok: true }>(`/favorites/${targetType}/${targetId}`, {
    method: "DELETE",
  });
}

// Geo
export type CitySuggestion = {
  id: string;
  label: string;
  city: string;
  region: string | null;
  country: string | null;
  countryCode: string | null;
  lat: number;
  lon: number;
};
export function getCitySuggestions(q: string, signal?: AbortSignal) {
  const params = new URLSearchParams({ q });
  return req<{ results: CitySuggestion[] }>(`/geo/cities?${params.toString()}`, { signal });
}

// Admin
export function adminListUsers(opts: { role?: string; pending?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.role) params.set("role", opts.role);
  if (opts.pending) params.set("pending", "true");
  const qs = params.toString();
  return req<{ users: User[] }>(`/admin/users${qs ? `?${qs}` : ""}`);
}
export function adminUpdateUser(
  id: string,
  input: { verified?: boolean; role?: "creator" | "business" | "admin" },
) {
  return req<{ user: User }>(`/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}
// Plan change — works for owner OR admin
export function updateBusinessPlan(id: string, plan: "free" | "pro") {
  return req<{ ok: true }>(`/businesses/${id}/plan`, {
    method: "PATCH",
    body: JSON.stringify({ plan }),
  });
}

// Admin: businesses
export type AdminBusiness = Business & { ownerEmail: string | null };
export type AdminBusinessStats = {
  totalPerks: number;
  activePerks: number;
  totalClaims: number;
  briefStatus: "draft" | "published" | null;
};
export function adminListBusinesses(opts: { plan?: "free" | "pro" } = {}) {
  const params = new URLSearchParams();
  if (opts.plan) params.set("plan", opts.plan);
  const qs = params.toString();
  return req<{ businesses: AdminBusiness[] }>(`/admin/businesses${qs ? `?${qs}` : ""}`);
}
export function adminGetBusiness(id: string) {
  return req<{ business: AdminBusiness; stats: AdminBusinessStats }>(`/admin/businesses/${id}`);
}

// Admin: perks on a business (bypasses Free-plan limits since admin is
// operating with full privilege, not as the business owner).
export function adminListBusinessPerks(businessId: string) {
  return req<{ perks: Perk[] }>(`/admin/businesses/${businessId}/perks`);
}
export function adminCreateBusinessPerk(
  businessId: string,
  input: CreatePerkInput,
) {
  return req<{ perk: Perk }>(`/admin/businesses/${businessId}/perks`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export function adminUpdateBusinessPerk(
  businessId: string,
  perkId: string,
  input: UpdatePerkInput,
) {
  return req<{ ok: true }>(
    `/admin/businesses/${businessId}/perks/${perkId}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

// Admin: brief for a business. Mirrors getMyBrief / createBrief /
// updateMyBrief but addresses a specific business id.
export function adminGetBusinessBrief(businessId: string) {
  return req<{ brief: Brief | null }>(`/admin/businesses/${businessId}/brief`);
}
export function adminCreateBusinessBrief(
  businessId: string,
  answers: BriefAnswers,
) {
  return req<{ brief: Brief }>(`/admin/businesses/${businessId}/brief`, {
    method: "POST",
    body: JSON.stringify(answers),
  });
}
export function adminUpdateBusinessBrief(
  businessId: string,
  input: UpdateBriefInput,
) {
  return req<{ brief: Brief }>(`/admin/businesses/${businessId}/brief`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export type AdminUserClaim = {
  id: string;
  perkId: string;
  status: "pending" | "approved" | "fulfilled" | "rejected";
  contentUrl: string | null;
  createdAt: string;
  perkTitle: string;
  businessId: string;
  businessName: string;
  businessCity: string | null;
};
export function adminGetUser(id: string) {
  return req<{ user: User; claims: AdminUserClaim[] }>(`/admin/users/${id}`);
}
export function adminUpdateUserProfile(id: string, input: UpdateProfileInput) {
  return req<{ user: User }>(`/admin/users/${id}/profile`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

// Businesses
export function createBusiness(input: CreateBusinessInput) {
  return req<{ business: Business }>("/businesses", {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export function getMyBusinesses() {
  return req<{ businesses: Business[] }>("/businesses/mine");
}
export function updateBusiness(id: string, input: UpdateBusinessInput) {
  return req<{ business: Business }>(`/businesses/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

// Perks
export function listPerks() {
  return req<{ perks: PerkWithBusiness[] }>("/perks");
}
export function listMyPerks() {
  return req<{ perks: Perk[] }>("/perks/mine");
}
export function createPerk(input: CreatePerkInput) {
  return req<{ perk: Perk }>("/perks", {
    method: "POST",
    body: JSON.stringify(input),
  });
}
export function updatePerk(id: string, input: UpdatePerkInput) {
  return req<{ ok: true }>(`/perks/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

// Claims
export type ClaimRow = Claim & {
  perkTitle: string;
  // My-claims-only: perk image and (once approved) the redemption code.
  perkImageUrl?: string | null;
  perkRedemptionCode?: string | null;
  businessName?: string;
  // Already present on Claim, repeated for clarity in views:
  // contentLinks and note are creator submissions on the claim itself.
  creatorEmail?: string;
  // Incoming-claims-only (business owner's view): the creator's profile so the
  // owner can see who's claiming before approving.
  creatorDisplayName?: string | null;
  creatorAvatarUrl?: string | null;
  creatorBio?: string | null;
  creatorCity?: string | null;
  creatorNeighborhood?: string | null;
  creatorVerified?: boolean;
  creatorVerificationRequestedAt?: string | null;
  creatorSocialLinks?: SocialLinks | null;
  creatorLinks?: Link[] | null;
};
export function claimPerk(perkId: string) {
  return req<{ claim: Claim }>(`/claims/perk/${perkId}`, { method: "POST" });
}
export function listMyClaims() {
  return req<{ claims: ClaimRow[] }>("/claims/mine");
}
export function listIncomingClaims() {
  return req<{ claims: ClaimRow[] }>("/claims/incoming");
}
export function updateClaim(id: string, input: UpdateClaimInput) {
  return req<{ ok: true }>(`/claims/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

// Briefs
export function getMyBrief() {
  return req<{ brief: Brief | null }>("/briefs/mine");
}
export function createBrief(answers: BriefAnswers) {
  return req<{ brief: Brief }>("/briefs", {
    method: "POST",
    body: JSON.stringify(answers),
  });
}
export function updateMyBrief(input: UpdateBriefInput) {
  return req<{ brief: Brief }>("/briefs/me", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}
export type PublicBriefBusiness = {
  id: string;
  name: string;
  slug: string;
  shortCode: string | null;
  city: string | null;
  address: string | null;
  description: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  phone: string | null;
  contactEmail: string | null;
  hours: import("@dropin/shared/schemas").BusinessHours | null;
  socialLinks: SocialLinks | null;
  lat: number | null;
  lon: number | null;
};
export type PublicBriefPerk = {
  id: string;
  title: string;
  description: string;
  active: boolean;
  imageUrl: string | null;
  requireApproval: boolean;
  createdAt: string;
};
export type PublicBriefResponse = {
  business: PublicBriefBusiness;
  brief: { id: string; businessId: string; content: string; publishedAt: string };
  perks: PublicBriefPerk[];
  viewerIsVerifiedCreator: boolean;
};
export function getPublicBrief(businessId: string) {
  return req<PublicBriefResponse>(`/briefs/business/${businessId}`);
}
